import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { attendanceAPI } from '../services/api';
import { addDaysLocalYmd, formatLocalDateYmd } from '../utils/localCalendarDate';

const MAX_RANGE_DAYS = 62;

async function blobResponseDetail(data) {
  if (!data) return null;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const json = JSON.parse(text);
        return json.detail || json.message || text;
      } catch {
        return text.slice(0, 300) || null;
      }
    } catch {
      return null;
    }
  }
  if (typeof data === 'object' && data.detail) return data.detail;
  return null;
}

function daysBetweenInclusive(fromYmd, toYmd) {
  const [y1, m1, d1] = fromYmd.split('-').map(Number);
  const [y2, m2, d2] = toYmd.split('-').map(Number);
  const a = new Date(y1, m1 - 1, d1);
  const b = new Date(y2, m2 - 1, d2);
  return Math.floor((b - a) / 86400000) + 1;
}

const SupervisorReports = () => {
  const today = formatLocalDateYmd();
  const monthStart = `${today.slice(0, 8)}01`;

  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [factoryId, setFactoryId] = useState('');
  const [employeeUserId, setEmployeeUserId] = useState('');
  const [supervisorFactories, setSupervisorFactories] = useState([]);
  const [loadingFactories, setLoadingFactories] = useState(true);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      setLoadingFactories(true);
      try {
        const res = await attendanceAPI.supervisorSites();
        setSupervisorFactories(res.data || []);
      } catch {
        setSupervisorFactories([]);
      } finally {
        setLoadingFactories(false);
      }
    })();
  }, []);

  const loadEmployeeOptions = useCallback(async () => {
    setLoadingEmployees(true);
    try {
      const params = { day: toDate };
      if (factoryId) params.factory_id = Number(factoryId);
      const res = await attendanceAPI.supervisorTeamBoard(params);
      const m = new Map();
      (res.data || []).forEach((r) => m.set(r.employee_user_id, r.employee_name));
      setEmployeeOptions(
        Array.from(m.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch {
      setEmployeeOptions([]);
    } finally {
      setLoadingEmployees(false);
    }
  }, [factoryId, toDate]);

  useEffect(() => {
    loadEmployeeOptions();
  }, [loadEmployeeOptions]);

  useEffect(() => {
    if (!employeeUserId) return;
    if (!employeeOptions.some((o) => String(o.id) === String(employeeUserId))) {
      setEmployeeUserId('');
    }
  }, [employeeUserId, employeeOptions]);

  const rangeDays = useMemo(() => daysBetweenInclusive(fromDate, toDate), [fromDate, toDate]);
  const rangeInvalid = toDate < fromDate || rangeDays > MAX_RANGE_DAYS;

  const downloadReport = async (format) => {
    setErr('');
    setMsg('');
    if (rangeInvalid) {
      setErr(
        toDate < fromDate
          ? 'La fecha hasta debe ser posterior o igual a la fecha desde.'
          : `El período máximo es de ${MAX_RANGE_DAYS} días.`
      );
      return;
    }
    setDownloading(format);
    try {
      const params = { from_date: fromDate, to_date: toDate, format };
      if (factoryId) params.factory_id = Number(factoryId);
      if (employeeUserId) params.employee_user_id = Number(employeeUserId);
      const res = await attendanceAPI.supervisorReportExport(params);
      const contentType = String(res.headers['content-type'] || '');
      if (contentType.includes('application/json')) {
        const detail = await blobResponseDetail(res.data);
        setErr(detail || 'No se pudo generar el reporte');
        return;
      }
      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([res.data], {
              type: format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8',
            });
      if (blob.size === 0) {
        setErr('El servidor devolvió un archivo vacío.');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `fichadas_${fromDate}_${toDate}.${format}`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMsg(`Descarga ${format.toUpperCase()} iniciada.`);
    } catch (e) {
      const detail = await blobResponseDetail(e?.response?.data);
      setErr(detail || e?.message || 'No se pudo generar el reporte');
    } finally {
      setDownloading('');
    }
  };

  const setPresetDays = (days) => {
    const end = formatLocalDateYmd();
    setToDate(end);
    setFromDate(addDaysLocalYmd(end, -(days - 1)));
  };

  return (
    <div className="space-y-4">
      <div className="card-mobile">
        <h2 className="text-lg font-semibold text-gray-900">Reportes de fichadas</h2>
        <p className="text-sm text-gray-500 mt-1 leading-relaxed">
          Descargá el historial de tu equipo en PDF o CSV. Podés filtrar por sede, empleado y rango de
          fechas (máx. {MAX_RANGE_DAYS} días).
        </p>
      </div>

      <div className="card-mobile space-y-4">
        {err && <p className="text-sm text-red-600">{err}</p>}
        {msg && <p className="text-sm text-green-700">{msg}</p>}

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-mobile btn-secondary text-xs py-1.5 min-h-0" onClick={() => setPresetDays(7)}>
            Últimos 7 días
          </button>
          <button type="button" className="btn-mobile btn-secondary text-xs py-1.5 min-h-0" onClick={() => setPresetDays(30)}>
            Últimos 30 días
          </button>
          <button
            type="button"
            className="btn-mobile btn-secondary text-xs py-1.5 min-h-0"
            onClick={() => {
              setFromDate(monthStart);
              setToDate(today);
            }}
          >
            Mes en curso
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="form-label-mobile" htmlFor="report-from">
              Desde
            </label>
            <input
              id="report-from"
              type="date"
              className="input-mobile w-full"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label-mobile" htmlFor="report-to">
              Hasta
            </label>
            <input
              id="report-to"
              type="date"
              className="input-mobile w-full"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>

        <p className={`text-xs ${rangeInvalid ? 'text-red-600' : 'text-slate-500'}`}>
          {toDate < fromDate
            ? 'Revisá el rango de fechas.'
            : `Período seleccionado: ${rangeDays} día${rangeDays === 1 ? '' : 's'}.`}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="form-label-mobile" htmlFor="report-factory">
              Sede
            </label>
            <select
              id="report-factory"
              className="input-mobile w-full"
              value={factoryId}
              onChange={(e) => setFactoryId(e.target.value)}
              disabled={loadingFactories}
            >
              <option value="">
                {loadingFactories ? 'Cargando sedes…' : 'Todas las sedes'}
              </option>
              {supervisorFactories.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
            {!loadingFactories && supervisorFactories.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">
                No hay sedes con asignaciones de supervisión. Pedile al dueño que confirme supervisor → empleado.
              </p>
            )}
          </div>
          <div>
            <label className="form-label-mobile" htmlFor="report-employee">
              Empleado
            </label>
            <select
              id="report-employee"
              className="input-mobile w-full"
              value={employeeUserId}
              onChange={(e) => setEmployeeUserId(e.target.value)}
              disabled={loadingEmployees || employeeOptions.length === 0}
            >
              <option value="">Todos los empleados</option>
              {employeeOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <button
            type="button"
            className="btn-mobile btn-primary flex-1"
            disabled={!!downloading || rangeInvalid}
            onClick={() => downloadReport('pdf')}
          >
            {downloading === 'pdf' ? 'Generando PDF…' : 'Descargar PDF'}
          </button>
          <button
            type="button"
            className="btn-mobile btn-secondary flex-1"
            disabled={!!downloading || rangeInvalid}
            onClick={() => downloadReport('csv')}
          >
            {downloading === 'csv' ? 'Generando CSV…' : 'Descargar CSV'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupervisorReports;
